import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { addQuantity } from "~/features/addToCart/redux/cartAmounts/addQuantity";
import { batch } from "react-redux";
import { userIsPurchaser, userIsSalesman } from "~/domains/user/utils/roles";
import { type EntryGroup, type LineItem, isPackPhaseoutGroup, isProductPhaseoutGroup } from "../../models/CartData";
import { convertProductToImpressionCart, trackRemoveFromCart } from "~/app/analytics/ga/ga";
import { send as deleteCart } from "../../api/ClearCart";
import { send as deleteQuote } from "~/features/cart/activeQuote/api/clearQuote";
import { type LineItemBuilder } from "../../../../../utils/cartUpdating/LineItemUpdate";
import {
  applyUpdates,
  buildCartMaterialNos,
  buildCartMaterialNosExcluding,
  newChangeObject,
} from "../../../../../utils/cartUpdating/common";
import { isNonNull, splitBy } from "~/common/utils/reducers";
import type { CartState } from "~/features/cart/cart/redux/types";
import { sendAddEntry, sendBulkEditEntry, sendDeleteEntry } from "../../api/cartQuoteCommonApi";
import { cancelPendingEntryUpdates } from "../../api/UpdatePackageEntry";
import { refreshPriceInfoData } from "../../../../../utils/cartUpdating/refreshPriceInfoData";
import { refreshDeliveriesForProduct } from "../../../../../utils/cartUpdating/delivery/refreshEdd";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { type RootState } from "~/app/store";
import { getDesciptorsForLineItems, trackCartUpdate } from "./actionUtils";
import { quotePackCodeFromEntries, resolveCartDescriptor } from "../../pna/cartPna";
import { validatePackage, validateQty } from "../../../../../utils/cartUpdating/validations/validateGeneralLineItem";
import { validateEntireProductQty } from "../../../../../utils/cartUpdating/validations/validateGeneralProduct";
import { apiDeleteCartSection } from "../../api/deleteCartSection";
import { selectLineItemsByProduct, selectPackageEntry } from "../selectors/cartSelectors";
import { getPricePerPiece } from "~/features/cart/cart/utils/lineItemPriceUtils";
import { type checkoutLoadDataAction } from "~/features/cart/cart/redux/actions/checkoutPageActions";
import { selectLineItemsForProduct } from "~/features/cart/cart/redux/selectors/productEntrySelectors";
import { trackCartContents, trackClearCart } from "~/app/analytics/ga/abandonment";
import { type cartLoadFinishAction } from "~/features/cart/cart/redux/actions";
import { type updateCartCommentAction } from "~/features/cart/cart/redux/actions/cartPageActions";
import {
  BLOCK_SCREEN,
  DELETE_PACKAGE_ENTRY,
  SET_PACKAGE_ENTRIES,
  UNBLOCK_SCREEN,
} from "~/features/cart/cart/redux/actions/actionTypes";
import { selectBundleGroups } from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";

import type { ProductImpression } from "~/app/analytics/ga/types";
import { apiDeleteProduct } from "../../api/deleteProduct";
import { getHybrisProductCode } from "~/utils/cartHelper";
import { invalidateMinicart } from "~/app/store/bffApi";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

/** Legacy QUOTE pack-code derivation per product, from the full cart line items (A07). */
function packCodeByProduct(state: RootState): Dict<string | undefined> {
  const map: Dict<string | undefined> = {};
  Object.entries(selectLineItemsByProduct(state)).forEach(([productCode, lineItems]) => {
    map[productCode] = quotePackCodeFromEntries(lineItems);
  });
  return map;
}

function findPredecessorProductCodesForDeletedEntries(
  deletedEntryIds: number[],
  entryGroups: EntryGroup[],
  entryGroupsByNumber: Record<number, EntryGroup>,
  packageEntriesById: Record<number, LineItem>,
): string[] {
  const deletedIds = new Set(deletedEntryIds);
  const predecessorPks = new Set<number>();

  for (const rootGroup of entryGroups) {
    for (const child of rootGroup.children ?? []) {
      if (isProductPhaseoutGroup(child)) {
        const successorAffected = child.successorGroupNumbers.some((gn) => {
          const g = entryGroupsByNumber[gn];
          if (!g || g.groupType !== "PRODUCT") {
            return false;
          }
          return Object.values(packageEntriesById).some((e) => e.groupNumbers.includes(gn) && deletedIds.has(e.pk));
        });

        if (successorAffected) {
          for (const predGn of child.predecessorGroupNumbers) {
            for (const entry of Object.values(packageEntriesById)) {
              if (entry.groupNumbers.includes(predGn) && !deletedIds.has(entry.pk)) {
                predecessorPks.add(entry.pk);
              }
            }
          }
          continue;
        }

        if (child.predecessorPackEntryPks) {
          const successorPks = child.successorGroupNumbers.flatMap((gn) => {
            const g = entryGroupsByNumber[gn];
            if (!g || g.groupType !== "PRODUCT") {
              return [];
            }
            return Object.values(packageEntriesById)
              .filter((e) => e.groupNumbers.includes(gn))
              .map((e) => e.pk);
          });

          if (successorPks.some((pk) => deletedIds.has(pk))) {
            for (const pk of child.predecessorPackEntryPks) {
              if (!deletedIds.has(pk)) {
                predecessorPks.add(pk);
              }
            }
          }
        }
        continue;
      }

      if (child.groupType !== "PRODUCT") {
        continue;
      }

      const packPhaseout = child.children?.find(isPackPhaseoutGroup);
      if (!packPhaseout) {
        continue;
      }

      const hasDeletedSuccessor = packPhaseout.successorPackEntryPks.some((pk) => deletedIds.has(pk));
      if (hasDeletedSuccessor) {
        for (const pk of packPhaseout.predecessorPackEntryPks) {
          if (!deletedIds.has(pk)) {
            predecessorPks.add(pk);
          }
        }
      }
    }
  }

  return [
    ...new Set(
      Object.values(packageEntriesById)
        .filter((e) => predecessorPks.has(e.pk))
        .map((e) => e.productCode),
    ),
  ];
}

export function deleteProductSaga(productCode: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();
    dispatch({ type: BLOCK_SCREEN, payload: "spinner.removeProduct" });
    const promise = apiDeleteProduct(getHybrisProductCode(productCode));
    // Await on the promise until the BE manages to remove all products from the section and only then update the UI
    await promise;
    dispatch(invalidateMinicart());

    const lineItemsByProduct = selectLineItemsByProduct(state);
    const deletedLineItems = lineItemsByProduct[productCode].map(newChangeObject);

    const productDescriptors = getDesciptorsForLineItems(state, productCode, deletedLineItems);
    const availability = Object.values(productDescriptors)[0]?.availability;
    if (availability) {
      refreshDeliveriesForProduct(deletedLineItems, availability);
    }

    if (userIsPurchaser()) {
      deletedLineItems.forEach((builder) => {
        const lineItem = state.cart.packageEntriesById[builder.get("id")];
        trackRemoval(state, productCode, lineItem);
      });
    }

    const deletedPks = new Set(deletedLineItems.map((item) => item.get("id")));
    const predecessorProductCodes = findPredecessorProductCodesForDeletedEntries(
      [...deletedPks],
      state.cart.entryGroups,
      state.cart.entryGroupsByNumber,
      state.cart.packageEntriesById,
    );
    const predecessorLineItemUpdates: LineItemBuilder[][] = [];
    if (predecessorProductCodes.length > 0) {
      const cartMaterialNos = buildCartMaterialNosExcluding(state, deletedPks);
      for (const predProductCode of predecessorProductCodes) {
        const predLineItems = selectLineItemsForProduct(state, { productCode: predProductCode }).map(newChangeObject);
        const predDescriptors = getDesciptorsForLineItems(state, predProductCode, predLineItems);
        const predDescriptor = Object.values(predDescriptors)[0];
        if (predDescriptor) {
          predLineItems.forEach(validateQty);
          validateEntireProductQty(predLineItems, predDescriptor, cartMaterialNos);
          predLineItems.forEach(validatePackage);
          predecessorLineItemUpdates.push(predLineItems);
        }
      }
    }

    // TODO: There shouldn't be so many actions dispatched. Should be one, somehow.
    // TODO: First thing to do is refactor-out the addQuantity action and entire "productAmounts" slice of state
    // Supersede pending updates for the deleted entries before they leave Redux state — a drained
    // batch must never ship PATCHes for entries the backend has already removed (task 0045).
    await cancelPendingEntryUpdates(deletedPks);
    batch(() => {
      deletedLineItems.forEach((builder) => {
        const lineItem = state.cart.packageEntriesById[builder.get("id")];
        dispatch(addQuantity(productCode, lineItem.packCode, -lineItem.quantity, lineItem.priceReference));

        dispatch(deletePackageEntryAction(lineItem.id));
      });

      for (const predLineItems of predecessorLineItemUpdates) {
        dispatch(sendToRedux(predLineItems));
      }
    });

    dispatch({ type: UNBLOCK_SCREEN });
  };
}

export function deleteSectionSaga(quoteNumber: string, cartId?: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();

    if (!quoteNumber) {
      // Are there any quote bundles at all?
      const hasBundles = selectBundleGroups(state).length > 0;
      if (!hasBundles) {
        // delete section in this situation is equivalent to deleting the entire cart.
        if (cartId || userIsSalesman()) {
          dispatch(clearCart(cartId));
        }
        return;
      }
    }

    dispatch({ type: BLOCK_SCREEN, payload: "spinner.removeProduct" });

    const promise = apiDeleteCartSection(quoteNumber);
    // Await on the promise until the BE manages to remove all products from the section and only then update the UI
    await promise;
    dispatch(invalidateMinicart());

    const lineItemsByProduct = selectLineItemsByProduct(state);
    // Supersede pending updates for every entry this section deletion removes, before they leave
    // Redux state — a drained batch must never ship PATCHes for removed entries (task 0045).
    const sectionDeletedIds = new Set(
      Object.values(lineItemsByProduct).flatMap((lineItems) => {
        const allLineItems = lineItems.map(newChangeObject);
        const filterFunc = quoteNumber
          ? (lineItem: LineItemBuilder) => lineItem.get("quoteNumber") === quoteNumber
          : (lineItem: LineItemBuilder) => !lineItem.get("isBundled");
        return allLineItems.filter(filterFunc).map((lineItem) => lineItem.get("id"));
      }),
    );
    await cancelPendingEntryUpdates(sectionDeletedIds);
    Object.entries(lineItemsByProduct).forEach(([productCode, lineItems]) => {
      const allLineItems = lineItems.map(newChangeObject);

      const filterFunc = quoteNumber
        ? (lineItem: LineItemBuilder) => lineItem.get("quoteNumber") === quoteNumber
        : (lineItem: LineItemBuilder) => !lineItem.get("isBundled");
      const allRemainingLineItems = allLineItems.filter((lineItem) => !filterFunc(lineItem));
      const lineItemsToDelete = allLineItems.filter((lineItem) => filterFunc(lineItem));

      const productDescriptors = getDesciptorsForLineItems(state, productCode, allLineItems);
      const availability = Object.values(productDescriptors)[0]?.availability;
      if (availability) {
        refreshDeliveriesForProduct(allRemainingLineItems, availability);
      }

      if (userIsPurchaser()) {
        lineItemsToDelete.forEach((builder) => {
          const lineItem = state.cart.packageEntriesById[builder.get("id")];
          trackRemoval(state, productCode, lineItem);
        });
      }

      // TODO: There shouldn't be so many actions dispatched. Should be one, somehow.
      // TODO: First thing to do is refactor-out the addQuantity action and entire "productAmounts" slice of state
      batch(() => {
        dispatch(sendToRedux(allRemainingLineItems));
        lineItemsToDelete.forEach((builder) => {
          const lineItem = state.cart.packageEntriesById[builder.get("id")];
          if (quoteNumber === undefined) {
            dispatch(addQuantity(productCode, lineItem.packCode, -lineItem.quantity, lineItem.priceReference));
          }

          dispatch(deletePackageEntryAction(lineItem.id));
        });
      });
    });

    trackCartContents(getState());

    dispatch({
      type: UNBLOCK_SCREEN,
    });
  };
}

export function deletePackageEntry(packageEntryId: number): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch({ type: BLOCK_SCREEN, payload: "spinner.removeProduct" });

    const state = getState();
    const { cart } = state;
    const lineItemToDelete = cart.packageEntriesById[packageEntryId];
    const { productCode } = lineItemToDelete;

    const allLineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);

    const allRemainingLineItems = allLineItems.filter((lineItem) => lineItem.get("id") !== packageEntryId);

    const productDescriptors = getDesciptorsForLineItems(state, productCode, allLineItems);
    refreshPriceInfoData(allRemainingLineItems, productDescriptors);

    const productDescriptor = productDescriptors[lineItemToDelete.priceReference];
    const availability = productDescriptor.availability;
    if (availability) {
      refreshDeliveriesForProduct(allRemainingLineItems, availability);
    }

    allRemainingLineItems.forEach(validateQty);
    validateEntireProductQty(allRemainingLineItems, productDescriptor, buildCartMaterialNos(state));
    allRemainingLineItems.forEach(validatePackage);

    const deletedPks = new Set([packageEntryId]);
    const predecessorProductCodes = findPredecessorProductCodesForDeletedEntries(
      [packageEntryId],
      cart.entryGroups,
      cart.entryGroupsByNumber,
      cart.packageEntriesById,
    );
    const predecessorLineItemUpdates: LineItemBuilder[][] = [];
    if (predecessorProductCodes.length > 0) {
      const cartMaterialNos = buildCartMaterialNosExcluding(state, deletedPks);
      for (const predProductCode of predecessorProductCodes) {
        const predLineItems = selectLineItemsForProduct(state, { productCode: predProductCode }).map(newChangeObject);
        const predDescriptors = getDesciptorsForLineItems(state, predProductCode, predLineItems);
        const predDescriptor = Object.values(predDescriptors)[0];
        if (predDescriptor) {
          predLineItems.forEach(validateQty);
          validateEntireProductQty(predLineItems, predDescriptor, cartMaterialNos);
          predLineItems.forEach(validatePackage);
          predecessorLineItemUpdates.push(predLineItems);
        }
      }
    }

    if (lineItemToDelete.entryNumber !== null) {
      sendDeleteEntry(lineItemToDelete, {
        productCode,
        priceReference: lineItemToDelete.priceReference,
        base: productDescriptor.base,
        packCode: packCodeByProduct(state)[productCode],
      })
        .catch((error: unknown) => {
          // An entire-cart/quote clear may epoch-cancel this delete mid-flight; the screen must
          // still unblock and the abort is expected, not an error worth reporting (task 0045).
          if (!(error instanceof DOMException && error.name === "AbortError")) {
            console.error(error);
          }
        })
        .finally(() => {
          dispatch(invalidateMinicart());
          dispatch({
            type: UNBLOCK_SCREEN,
          });
        });

      if (userIsPurchaser()) {
        trackRemoval(state, productCode, lineItemToDelete);
      }
    }

    batch(() => {
      dispatch(sendToRedux(allRemainingLineItems));
      dispatch(
        addQuantity(
          productCode,
          lineItemToDelete.packCode,
          -lineItemToDelete.quantity,
          lineItemToDelete.priceReference,
        ),
      );

      dispatch(deletePackageEntryAction(packageEntryId));

      for (const predLineItems of predecessorLineItemUpdates) {
        dispatch(sendToRedux(predLineItems));
      }
    });
  };
}

function deletePackageEntryAction(packageEntryId: number) {
  return {
    type: DELETE_PACKAGE_ENTRY,
    payload: {
      packageEntryId,
    },
  } as const;
}

function trackRemoval(state: RootState, productCode: string, lineItem: LineItem) {
  const productDescriptor = resolveCartDescriptor(
    state,
    productCode,
    lineItem.priceReference,
    undefined,
    quotePackCodeFromEntries(selectLineItemsForProduct(state, { productCode })),
  );
  const pricePerPiece = getPricePerPiece(lineItem);
  const productImpression = convertProductToImpressionCart(
    productDescriptor.base,
    lineItem,
    productDescriptor.availability,
    pricePerPiece,
  );
  trackRemoveFromCart([productImpression]);
}

export function clearCart(cartId?: string): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    const isSalesman = userIsSalesman();
    if (!isSalesman && !cartId) {
      return Promise.reject(new Error("Cannot clear a cart without its explicit resource identity"));
    }
    const deletionPromise = isSalesman ? deleteQuote() : deleteCart(cartId!);

    if (!isSalesman) {
      const state = getState();
      const packEntries = Object.values(state.cart.packageEntriesById);
      const packCodes = packCodeByProduct(state);
      const impressionsMap = packEntries
        .map((packEntry) => {
          const productDescriptor = resolveCartDescriptor(
            state,
            packEntry.productCode,
            packEntry.priceReference,
            undefined,
            packCodes[packEntry.productCode],
          );
          return convertProductToImpressionCart(
            productDescriptor.base,
            packEntry,
            productDescriptor.availability,
            getPricePerPiece(packEntry),
          );
        })
        .reduce(
          (map, impression) => {
            if (!map[impression.id]) {
              map[impression.id] = impression;
            } else {
              map[impression.id].quantity = (map[impression.id].quantity || 0) + (impression.quantity || 0);
            }
            return map;
          },
          {} as { [id: string]: ProductImpression },
        );

      trackRemoveFromCart(Object.values(impressionsMap));
    }

    dispatch({ type: BLOCK_SCREEN });

    deletionPromise
      .then(() => {
        dispatch(invalidateMinicart());
        dispatch(clearCartAction());
      })
      .finally(() => dispatch({ type: UNBLOCK_SCREEN }));

    if (!isSalesman) {
      trackClearCart();
    }

    return deletionPromise;
  };
}

function internalUpdateQuantity(qty: number, packageEntryId: number): ThunkResult<LineItemBuilder[]> {
  return (dispatch, getState) => {
    const state = getState();

    const packageEntry = state.cart.packageEntriesById[packageEntryId];
    if (!packageEntry) {
      return [];
    }
    const productCode = packageEntry.productCode;

    const allLineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);

    const productDescriptors = getDesciptorsForLineItems(state, productCode, allLineItems);

    const editedLineItem = allLineItems.find((item) => item.get("pk") === packageEntryId);
    if (!editedLineItem) {
      return [];
    }

    const nonContractLineItems = allLineItems.filter((item) => !item.get("contractNumber"));

    editedLineItem.set("quantity", qty);

    refreshPriceInfoData(allLineItems, productDescriptors);

    const productDescriptor = Object.values(productDescriptors)[0];
    const availability = productDescriptor.availability;
    if (availability) {
      refreshDeliveriesForProduct(nonContractLineItems, availability);
    }

    validateQty(editedLineItem);
    validateEntireProductQty(allLineItems, productDescriptor, buildCartMaterialNos(state));

    return allLineItems;
  };
}

export function updateQuantity(qty: number, packageEntryId: number): ThunkResult<void> {
  return async (dispatch) => {
    const allLineItems = dispatch(internalUpdateQuantity(qty, packageEntryId));

    dispatch(sendToRedux(allLineItems));

    dispatch(sendToHybris(allLineItems));
  };
}

export function buildSetPackageEntriesPayload(
  lineItems: LineItemBuilder[],
  cart: CartState,
): { packageEntry: LineItem; oldPackageEntry?: LineItem; productCode: string }[] {
  const itemsToUpdate = lineItems.filter((item) => item.hasUpdates());
  if (itemsToUpdate.length === 0) {
    return [];
  }

  return lineItems
    .map((item) => ({
      item: item.build(),
      productCode: item.getProductCode(),
    }))
    .filter(({ item }) => isNonNull(item))
    .map(({ item, productCode }) => {
      const packageEntry = item as LineItem;
      const oldPackageEntry = cart.packageEntriesById[packageEntry.id];
      return { packageEntry, oldPackageEntry, productCode };
    });
}

export function sendToRedux(lineItems: LineItemBuilder[]): ThunkResult<void> {
  return (dispatch, getState) => {
    const data = buildSetPackageEntriesPayload(lineItems, getState().cart);
    if (data.length === 0) {
      return;
    }
    dispatch(setPackageEntriesAction(data));
  };
}

function setPackageEntriesAction(data: { packageEntry: LineItem; oldPackageEntry?: LineItem; productCode: string }[]) {
  return {
    type: SET_PACKAGE_ENTRIES,
    payload: data,
  } as const;
}

export const updatePackageEntryPk = (packageEntryId: number, pk: number) => updatePackageEntry(packageEntryId, { pk });

export const updateDate = (date: Date, packageEntryId: number) =>
  updatePackageEntry(
    packageEntryId,
    { deliveryDate: date, manuallySetRDD: true },
    { cascadeToOptionalParts: true, updateDeliveries: true },
  );

export const clearManualDate = (packageEntryId: number): ThunkResult<void> => {
  return (dispatch, getState) => {
    const edd = getState().cart.packageEntriesById[packageEntryId].edd;
    let rdd = null;
    if (edd?.type === "estimate") {
      rdd = edd.date;
    }

    return dispatch(
      updatePackageEntry(packageEntryId, { deliveryDate: rdd, manuallySetRDD: false }, { updateDeliveries: true }),
    );
  };
};

export const updateCartTargetPrice = (targetPrice: PriceData | undefined, packageEntryId: number) => {
  const updates: Partial<LineItem> = targetPrice === undefined ? { targetPrice: undefined } : { targetPrice };

  return updatePackageEntry(packageEntryId, updates);
};

export const updateComment = (comment: string, packageEntryId: number) =>
  updatePackageEntry(packageEntryId, { comment });

export const updateInternalComment = (comment: string, packageEntryId: number) =>
  updatePackageEntry(packageEntryId, { internalComment: comment });

export function toggleCommentVisibility(packageEntryId: number): ThunkResult<void> {
  return (dispatch, getState) => {
    const newShowComments = !getState().cart.packageEntriesById[packageEntryId].showComments;
    dispatch(updatePackageEntry(packageEntryId, { showComments: newShowComments }));
  };
}

export const updateOfferPrice = (packageId: number, newPrice?: PriceData) =>
  updatePackageEntry(packageId, {
    offerPrice: newPrice,
    priceOverriden: !!newPrice,
  });

export const updateTargetPrice = (packageId: number, newPrice?: PriceData) =>
  updatePackageEntry(packageId, { targetPrice: newPrice });

interface UpdateOptions {
  /**
   * Whether the update should affect all optional parts as well
   */
  cascadeToOptionalParts?: boolean;
  updateDeliveries?: boolean;
}

export function updatePackageEntry(
  packageEntryId: number,
  updates: Partial<LineItem>,
  options?: UpdateOptions,
): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    const state = getState();
    const packageEntry = selectPackageEntry(packageEntryId)(state);
    // Quick-entry rows are not published before saving; stale row callbacks can also arrive after reload.
    if (!packageEntry) {
      return Promise.resolve();
    }

    const allLineItems = selectLineItemsForProduct(state, { productCode: packageEntry.productCode }).map(
      newChangeObject,
    );
    const productDescriptors = getDesciptorsForLineItems(state, packageEntry.productCode, allLineItems);
    const editedLineItem = allLineItems.find((item) => item.get("pk") === packageEntryId);
    if (!editedLineItem) {
      return Promise.resolve();
    }
    applyUpdates(editedLineItem, updates as Partial<LineItem>);

    let list;
    if (options?.updateDeliveries) {
      const availability = Object.values(productDescriptors)[0].availability;
      if (availability) {
        refreshDeliveriesForProduct(allLineItems, availability);
      }

      list = [...allLineItems];
    } else {
      list = [editedLineItem];
    }

    if (options?.cascadeToOptionalParts) {
      // No-op: KIT optional parts cascade is handled by the BE tree structure.
      // The `cascadeToOptionalParts` option is preserved for call-site compatibility.
    }

    dispatch(sendToRedux(list));
    return dispatch(sendToHybris(list));
  };
}

export function sendToHybris(lineItems: LineItemBuilder[]): ThunkResult<Promise<unknown>> {
  return async (dispatch, getState) => {
    if (lineItems.length === 0) {
      return;
    }

    const state = getState();
    const packCodes = packCodeByProduct(state);

    const packageEntries = lineItems.map((item) => item.buildForSending()).filter(isNonNull);
    // eslint-disable-next-line prefer-const
    let [newEntries, editedEntries] = splitBy(packageEntries, (item) => item.pk < 0);

    for (const item of newEntries) {
      const descriptor = resolveCartDescriptor(
        state,
        item.productCode,
        item.priceReference,
        undefined,
        packCodes[item.productCode],
      );
      await sendAddEntry(item, descriptor, dispatch)
        .then((pk) => dispatch(updatePackageEntryPk(item.id, pk)))
        .catch(console.error);
    }

    let res;
    if (editedEntries.length > 0) {
      if (!userIsSalesman()) {
        lineItems
          .filter((item) => item.get("pk") >= 0)
          .forEach((item) => {
            const descriptor = resolveCartDescriptor(
              state,
              item.getProductCode(),
              item.get("priceReference"),
              undefined,
              packCodes[item.getProductCode()],
            );
            trackCartUpdate(item, descriptor);
          });
      }

      editedEntries = editedEntries.filter((item) => {
        const itemFromState = selectPackageEntry(item.id)(state);
        if (!itemFromState) {
          console.debug(`Skipping update to line entry ${item.id} as it is missing from the state`);
          return false;
        }
        return true;
      });

      try {
        await sendBulkEditEntry(editedEntries, {});
        dispatch(invalidateMinicart());
        res = true;
      } catch (e) {
        console.error(e);
        res = false;
      }
    } else {
      res = true;
    }

    trackCartContents(getState());

    return res;
  };
}

function clearCartAction() {
  return {
    type: "CLEAR_CART",
  } as const;
}

export type CartActions =
  | ReturnType<typeof setPackageEntriesAction>
  | ReturnType<typeof deletePackageEntryAction>
  | ReturnType<typeof checkoutLoadDataAction>
  | ReturnType<typeof cartLoadFinishAction>
  | ReturnType<typeof clearCartAction>
  | ReturnType<typeof updateCartCommentAction>;
